/**
 * Word Batch Edit Operations
 *
 * Orchestrates batch and single edit application by routing to specialized handlers.
 * Uses document-wide search with 2-tier fallback (LENIENT → STRICT).
 *
 * CS-259: Supports INSERT/DELETE paragraph operations with index adjustment.
 */

/* global Word */

import {
  EditOperation,
  EditApplicationResult,
  SearchOperation,
  EditOperationType,
} from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import {
  searchDocument,
  searchWithinParagraph,
  selectOccurrence,
  searchWithinRange,
} from "./wordDocumentSearch";
import { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";
import { ParagraphApplyContext } from "./applyContext";
import { applyInsertOperation, applyDeleteOperation } from "./wordInsertDeleteOperations";
import { applyTextEditOperation, applyTextEditInBatch } from "./wordTextEditOperations";
import { applyListChangeEdit } from "./wordListStructureOperations";
import { resolveListChange } from "./listChangeHelpers";

const log = createLogger("WordBatchEditOperations");

/**
 * Applies multiple edits in a single batch operation
 *
 * Routes to appropriate handlers based on operationType:
 * - 'edit': Text modifications via searchPhrase (default)
 * - 'insert': Create new paragraphs via paragraphId
 * - 'delete': Remove paragraphs via paragraphId
 *
 * @param edits - Array of edit operations to apply
 * @param operationType - Type of operation from AutoRedlineOutput
 * @param context - Word request context
 * @param startTime - Operation start time for duration tracking
 * @param apply - Shared per-apply context (paragraphId, tracker, snapshot, message)
 * @returns Promise of edit application results
 */
export async function applyBatchEdits(
  edits: EditOperation[],
  operationType: EditOperationType = "edit",
  context: Word.RequestContext,
  startTime: number,
  apply: ParagraphApplyContext = {}
): Promise<EditApplicationResult[]> {
  const { paragraphId, indexTracker } = apply;
  const results: EditApplicationResult[] = [];

  log.debug(`Applying batch of ${edits.length} edits`, { operationType });

  // For INSERT/DELETE, apply each edit individually (they require sync between operations)
  if (operationType === "insert" || operationType === "delete") {
    // Validate paragraphId is provided for INSERT/DELETE operations
    if (paragraphId === undefined) {
      const error = `${operationType.toUpperCase()} operation requires paragraphId`;
      log.error(`Batch ${operationType} failed: ${error}`);
      return edits.map((edit) => ({
        success: false,
        editId: edit.editId,
        error,
        duration: Date.now() - startTime,
      }));
    }

    for (const edit of edits) {
      const result =
        operationType === "insert"
          ? await applyInsertOperation(edit, context, startTime, paragraphId, apply)
          : await applyDeleteOperation(edit, context, startTime, paragraphId, apply);
      results.push(result);
    }
    return results;
  }

  // Defensive: list-structure edits run their own context.sync() calls and must
  // never share the batch's deferred single sync. groupEditsBySyncRequirement
  // already isolates them into single groups, but if one reaches here, apply each
  // edit individually via applySingleEdit instead of the batch text path.
  if (edits.some((e) => resolveListChange(e) !== undefined)) {
    for (const edit of edits) {
      results.push(await applySingleEdit(edit, operationType, context, startTime, apply));
    }
    return results;
  }

  // For EDIT operations, use batch search approach (pass paragraphId for scoped search)
  const searchOperations = await performBatchSearch(
    edits,
    context,
    startTime,
    results,
    paragraphId,
    indexTracker
  );
  await selectOccurrences(searchOperations, context, startTime, results);
  await applyBatchTextEdits(searchOperations, startTime, results);

  // Single sync for all edit applications
  await context.sync();

  log.info(`Batch of ${edits.length} edits completed`, {
    successful: results.filter((r) => r.success).length,
    failed: results.filter((r) => !r.success).length,
  });

  return results;
}

/**
 * Phase 1: Search for all edits in the batch
 *
 * Supports both document-wide and paragraph-scoped search:
 * - If paragraphId is provided, searches within that paragraph only
 * - Otherwise, searches the entire document (backward compatible)
 */
async function performBatchSearch(
  edits: EditOperation[],
  context: Word.RequestContext,
  startTime: number,
  results: EditApplicationResult[],
  paragraphId?: number,
  indexTracker?: UnifiedIndexTracker
): Promise<SearchOperation[]> {
  const searchOperations: SearchOperation[] = [];

  for (const edit of edits) {
    log.debug(`Searching for ${edit.editId}`, {
      phraseLength: edit.searchPhrase.length,
      searchPhraseOccurrence: edit.searchPhraseOccurrence,
      searchScope: paragraphId !== undefined ? `paragraph ${paragraphId}` : "document-wide",
    });

    try {
      let searchResult;

      if (paragraphId !== undefined) {
        // Paragraph-scoped search with index adjustment
        const adjustedIndex = indexTracker
          ? indexTracker.calculateAdjustedIndexForEdit(paragraphId)
          : paragraphId;

        searchResult = await searchWithinParagraph(context, adjustedIndex, edit.searchPhrase);
      } else {
        // Document-wide search (backward compatible)
        searchResult = await searchDocument(context, edit.searchPhrase);
      }

      searchOperations.push({
        edit,
        searchResults: searchResult.results,
        usedFallback: searchResult.usedFallback,
      });

      log.debug(`Search completed for ${edit.editId}`, {
        occurrencesFound: searchResult.totalOccurrences,
        searchType: searchResult.usedFallback ? "STRICT" : "LENIENT",
        searchScope: paragraphId !== undefined ? `paragraph ${paragraphId}` : "document-wide",
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      log.error(`Search failed for ${edit.editId}`, { error: errorMsg });

      results.push({
        success: false,
        editId: edit.editId,
        error: errorMsg,
        duration: Date.now() - startTime,
      });
    }
  }

  return searchOperations;
}

/**
 * Phase 2: Select occurrences and prepare target searches
 */
async function selectOccurrences(
  searchOperations: SearchOperation[],
  context: Word.RequestContext,
  startTime: number,
  results: EditApplicationResult[]
): Promise<void> {
  for (const op of searchOperations) {
    const { edit, searchResults, usedFallback } = op;

    try {
      const selectedRange = selectOccurrence(
        searchResults,
        edit.searchPhraseOccurrence,
        edit.searchPhrase,
        edit.editId
      );

      op.searchPhraseRange = selectedRange;

      log.debug(`Selected occurrence ${edit.searchPhraseOccurrence} for ${edit.editId}`, {
        totalFound: searchResults.items.length,
        usedFallback,
      });

      // Prepare target search for replace operations
      if (edit.position === "replace" && edit.targetText) {
        const targetSearch = await searchWithinRange(
          selectedRange,
          edit.targetText,
          edit.matchWholeWord ?? false,
          context
        );
        op.targetSearch = targetSearch;

        log.debug(`Target search for ${edit.editId}`, {
          targetText: edit.targetText,
          occurrencesFound: targetSearch.items.length,
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      log.error(`Failed to select occurrence for ${edit.editId}`, {
        error: errorMsg,
        searchPhraseOccurrence: edit.searchPhraseOccurrence,
        foundOccurrences: searchResults.items.length,
      });

      results.push({
        success: false,
        editId: edit.editId,
        error: errorMsg,
        duration: Date.now() - startTime,
      });
    }
  }
}

/**
 * Phase 3: Apply all text edits
 */
async function applyBatchTextEdits(
  searchOperations: SearchOperation[],
  startTime: number,
  results: EditApplicationResult[]
): Promise<void> {
  for (const op of searchOperations) {
    const { edit, searchPhraseRange, targetSearch } = op;

    // Skip if selection failed
    if (!searchPhraseRange) {
      continue;
    }

    try {
      const editResult = applyTextEditInBatch(edit, searchPhraseRange, targetSearch);

      if (!editResult.success) {
        results.push({
          success: false,
          editId: edit.editId,
          error: editResult.error,
          duration: Date.now() - startTime,
        });
        continue;
      }

      results.push({
        success: true,
        editId: edit.editId,
        duration: Date.now() - startTime,
      });

      log.debug(`Edit ${edit.editId} queued for application`);
    } catch (editError) {
      const errorMsg = editError instanceof Error ? editError.message : "Unknown error";
      results.push({
        success: false,
        editId: edit.editId,
        error: errorMsg,
        duration: Date.now() - startTime,
      });
      log.error(`Failed to apply edit ${edit.editId}`, { error: errorMsg });
    }
  }
}

/**
 * Apply a single edit to the document
 *
 * Routes to appropriate handler based on operationType:
 * - 'edit': Text modifications via searchPhrase (default)
 * - 'insert': Create new paragraph via paragraphId
 * - 'delete': Remove paragraph via paragraphId
 *
 * @param edit - The edit operation to apply
 * @param operationType - Type of operation from AutoRedlineOutput
 * @param context - Word request context
 * @param startTime - Operation start time for duration tracking
 * @param apply - Shared per-apply context (paragraphId, tracker, snapshot, message)
 * @returns Promise of edit application result
 */
export async function applySingleEdit(
  edit: EditOperation,
  operationType: EditOperationType = "edit",
  context: Word.RequestContext,
  startTime: number,
  apply: ParagraphApplyContext = {}
): Promise<EditApplicationResult> {
  const { paragraphId, indexTracker } = apply;

  log.debug(`Applying single edit ${edit.editId}`, {
    operationType,
    position: edit.position,
    requiresSync: edit.requiresSync,
    paragraphId,
  });

  try {
    // Route based on operationType
    if (operationType === "insert") {
      if (paragraphId === undefined) {
        return {
          success: false,
          editId: edit.editId,
          error: "INSERT operation requires paragraphId",
          duration: Date.now() - startTime,
        };
      }
      return await applyInsertOperation(edit, context, startTime, paragraphId, apply);
    }

    if (operationType === "delete") {
      if (paragraphId === undefined) {
        return {
          success: false,
          editId: edit.editId,
          error: "DELETE operation requires paragraphId",
          duration: Date.now() - startTime,
        };
      }
      return await applyDeleteOperation(edit, context, startTime, paragraphId, apply);
    }

    // Default: 'edit' operation. May carry a list-structure change (listChange),
    // a text change, or both.
    const listChange = resolveListChange(edit);
    if (listChange) {
      const structureOnly = !edit.searchPhrase;
      if (structureOnly) {
        return await applyListChangeEdit(
          edit,
          listChange,
          context,
          startTime,
          paragraphId,
          indexTracker
        );
      }
      // Text + structure: apply the text edit first; only restructure if it succeeded.
      const textResult = await applyTextEditOperation(
        edit,
        context,
        startTime,
        paragraphId,
        indexTracker
      );

      if (!textResult.success) return textResult;

      return await applyListChangeEdit(
        edit,
        listChange,
        context,
        startTime,
        paragraphId,
        indexTracker
      );
    }

    // Pure text edit (pass paragraphId for scoped search)
    return await applyTextEditOperation(edit, context, startTime, paragraphId, indexTracker);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    log.error(`Failed to apply single edit ${edit.editId}`, {
      error: errorMsg,
      searchPhrase: edit.searchPhrase,
    });

    return {
      success: false,
      editId: edit.editId,
      error: errorMsg,
      duration: Date.now() - startTime,
    };
  }
}
